/**
 * CopyableValue — Iter52 P15
 *
 * Wrapper inline pentru valori care trebuie copiate frecvent (UUID-uri, POD,
 * CNP, NLC, numere de contract, etc.). Hover → afișează iconul Copy. Click →
 * copy + toast + check icon timp de 1.2s.
 *
 * Usage:
 *   <CopyableValue value={sale.id} mono />
 *   <CopyableValue value={client.cnp} label="CNP" />
 *   <CopyableValue value={contract.pod} mono truncate />
 */
import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { EmptyValue } from "./EmptyValue";

export const CopyableValue = ({
  value,
  mono = false,
  truncate = false,
  label,
  className = "",
  testId,
}) => {
  const [copied, setCopied] = useState(false);

  if (value === null || value === undefined || value === "") {
    return <EmptyValue />;
  }
  const str = String(value);
  const display = truncate && str.length > 14
    ? `${str.slice(0, 6)}…${str.slice(-4)}`
    : str;

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      // Modern API + fallback
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(str);
      } else {
        const ta = document.createElement("textarea");
        ta.value = str;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(label ? `${label} copiat` : "Copiat în clipboard", { duration: 1200 });
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Nu am putut copia (folosește Ctrl+C)");
    }
  };

  return (
    <span
      className={
        `group inline-flex items-center gap-1 cursor-pointer rounded px-1 -mx-1 hover:bg-slate-100 transition ${
          mono ? "font-mono" : ""
        } ${className}`
      }
      onClick={handleCopy}
      title={`Click pentru a copia: ${str}`}
      data-testid={testId || "copyable"}
    >
      <span className="truncate">{display}</span>
      {copied ? (
        <Check className="h-3 w-3 text-emerald-600 flex-shrink-0" />
      ) : (
        <Copy className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition flex-shrink-0" />
      )}
    </span>
  );
};

export default CopyableValue;
